import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/admin/roles'),
        api.get('/admin/permissions'),
      ]);
      setRoles(rolesRes.data.data || []);
      setAllPermissions(permsRes.data.data || {});
    } catch (e) { toast.error('Failed to load roles'); }
  };

  useEffect(() => { fetchData(); }, []);

  const togglePerm = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const openEdit = (role) => {
    setEditRole(role);
    setForm({ name: role.name, description: role.description || '', permissions: role.permissions || [] });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditRole(null);
    setForm({ name: '', description: '', permissions: [] });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editRole) {
        await api.put(`/admin/roles/${editRole.id}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/admin/roles', form);
        toast.success('Role created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const permGroups = {};
  Object.entries(allPermissions).forEach(([key, desc]) => {
    const [group] = key.split('.');
    if (!permGroups[group]) permGroups[group] = [];
    permGroups[group].push({ key, desc });
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Roles & Permissions</h1>
          <p className="text-muted-foreground mt-1">Manage access control roles</p>
        </div>
        <Button onClick={openCreate} data-testid="role-create-button"><Plus className="h-4 w-4 mr-2" /> New Role</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Permissions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.id} className="hover:bg-muted/60">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      {r.name}
                      {r.is_system && <Badge variant="outline" className="text-[10px]">System</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.permissions?.length || 0}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`role-edit-${r.id}`}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>Configure role name and permissions</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="role-name-input" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            </div>
            <div className="space-y-4">
              <Label>Permissions ({form.permissions.length} selected)</Label>
              {Object.entries(permGroups).map(([group, perms]) => (
                <div key={group} className="border rounded-lg p-3">
                  <p className="text-sm font-medium capitalize mb-2">{group}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {perms.map(({ key, desc }) => (
                      <label key={key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1">
                        <Checkbox checked={form.permissions.includes(key)} onCheckedChange={() => togglePerm(key)} />
                        <span className="text-muted-foreground">{desc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="role-submit-button">
              {loading ? 'Saving...' : editRole ? 'Update Role' : 'Create Role'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
